import React, { useCallback } from 'react';
import { logChatPromiseExecution, MessageResponse } from 'stream-chat';
import {
  Channel,
  defaultPinPermissions,
  MessageInput,
  MessageList,
  MessageToSend,
  PinEnabledUserRoles,
  Thread,
  useChannelActionContext,
  useChannelStateContext,
  Window,
} from 'stream-chat-react';

import { PinnedMessageList } from '../PinnedMessageList/PinnedMessageList';
import { TeamChannelHeader } from '../TeamChannelHeader/TeamChannelHeader';
import { ThreadMessageInput } from '../TeamMessageInput/ThreadMessageInput';

import { useGiphyInMessageContext } from '../../context/GiphyInMessageFlagContext';

import type { StreamChatType } from '../../types';

type ChannelInnerProps = {
  popout?: boolean;
};

export const ChannelInner = ({ popout = false }: ChannelInnerProps) => {
  const { inputHasGiphyMessage, clearGiphyFlag } = useGiphyInMessageContext();
  const { sendMessage } = useChannelActionContext<StreamChatType>();
  const { channel } = useChannelStateContext<StreamChatType>();

  // todo: migrate to channel capabilities once migration guide is available
  const teamPermissions: PinEnabledUserRoles = { ...defaultPinPermissions.team, user: true };
  const messagingPermissions: PinEnabledUserRoles = {
    ...defaultPinPermissions.messaging,
    user: true,
  };

  const pinnedPermissions = {
    ...defaultPinPermissions,
    team: teamPermissions,
    messaging: messagingPermissions,
  };

  const overrideSubmitHandler = useCallback((message: MessageToSend<StreamChatType>) => {
    let updatedMessage = {
      attachments: message.attachments,
      mentioned_users: message.mentioned_users,
      parent_id: message.parent?.id,
      parent: message.parent as MessageResponse<StreamChatType>,
      text: message.text,
    };

    const isReply = !!updatedMessage.parent_id;

    if (inputHasGiphyMessage(isReply)) {
      const updatedText = `/giphy ${message.text}`;
      updatedMessage = { ...updatedMessage, text: updatedText };
    }

    if (sendMessage) {
      const sendMessagePromise = sendMessage(updatedMessage);
      logChatPromiseExecution(sendMessagePromise, 'send message');
      clearGiphyFlag(isReply);
    }
  }, [inputHasGiphyMessage, sendMessage, clearGiphyFlag]);

  return (
    <Channel channel={channel}>
      <Window>
        <TeamChannelHeader popout={popout} />
        <MessageList />
        <MessageInput grow overrideSubmitHandler={overrideSubmitHandler} />
      </Window>
      <Thread additionalMessageInputProps={{ grow: true, Input: ThreadMessageInput, overrideSubmitHandler }} />
      <PinnedMessageList />
    </Channel>
  );
};
